(ns admin.containers.activity.core
  (:require
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [kyber.styles.core :refer [gs styles->classes]]

   [kyber.components.composite.footer :refer [footer]]
   [kyber.components.composite.fullscreen-toggle :as fullscreen-toggle]
   [kyber.components.base.icons :as icons]
   [kyber.components.base.text :as text]
   [kyber.components.composite.loading :as loading]

   [admin.containers.activity.advanced :as activity-advanced]
   [admin.containers.activity.boost :as activity-boost]
   [admin.containers.activity.boosts :as activity-boosts]
   [admin.containers.activity.moments :as activity-moments]
   [admin.containers.activity.overview :as activity-overview]
   [admin.containers.activity.questionnaire :as activity-questionnaire]
   [admin.containers.activity.resources :as activity-resources]
   [admin.containers.activity.settings :as activity-settings]

   [admin.events.assessments :as >assessments]
   [admin.events.evaluations :as >evaluations]
   [admin.events.floating :as >floating]
   [admin.events.questionnaires :as >questionnaires]


   [admin.containers.looker-dashboards :as looker-dashboards]
   [admin.containers.looker-dashboard :as looker-dashboard]

   [admin.containers.line-manager-report :as line-manager-report]
   [admin.containers.director-report :as director-report]

   [admin.components.composite.activity-boost-workflow :as activity-boost-workflow]
   [admin.components.composite.resource-viewer :as resource-viewer]
   [admin.components.composite.curriculum.banner :as banner]
   [admin.router.utils :as aru]

   [kyber.containers.not-found :as not-found]
   [kyber.lib.kyber-db :as kyber-db]
   [kyber.utils :as ku]))

(def sidebar-width 230)

(def ^:private route-key->title
  {:activity-overview "Overview"
   :activity-resources "Resources"
   :activity-moments "Moments"
   :activity-assessments "Assessments"
   :activity-evaluation "Evaluation"
   :activity-advanced "Integrations"
   :activity-boosts "Boosts"
   :activity-boost-step-workflow "Create Boost"
   :activity-settings "Settings"
   :edit-activity-boost "Edit Boost"
   :edit-activity-boost-questionnaire "Edit Boost Questions"
   :dashboard-list "Dashboards"
   :dashboard "Dashboards"
   :reports-line-manager "Line Manager"
   :reports-director "Manager Reports"})

(def classes
  (styles->classes
   {:page-container
    {:flex-direction :column
     :background-color (gs [:colors :true-white])}

    :full-height
    {:display :flex
     :flex "1 1 auto"}

    :items-center
    {:display :flex
     :align-items :center}

    :title-bar
    {:background-color (gs [:colors :true-white])
     :position :fixed
     :right 0
     :z-index 2
     :height (gs [:spacing :p48])
     :transition "left 0.25s ease-in-out"
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))}

    :divider
    {:border-right (str "1px solid " (gs [:colors :gray-scale-200]))
     :display :block
     :width "1px"
     :height (gs [:spacing :p48])
     :margin [(gs [:spacing :p0])
              (gs [:spacing :p20])]}

    :true-white
    {:color (gs [:colors :true-white])}

    :sidebar
    {:min-width (str sidebar-width "px")
     :background (gs [:colors :gray-scale-700])
     :position :absolute
     :top 0
     :bottom 0
     :overflow-y :auto
     :z-index 1
     :transition "0.25s ease-in-out"}

    :section-container
    {:margin-top (gs [:spacing :p48])
     :display :flex
     :flex "1 1 auto"}

    :section-inner
    {:display :flex
     :flex-direction :column
     :flex-grow 1
     :width "100%"
     "& > *"
     {:width "100%"}}

    :moments-container
    {"& > div"
     {:position :absolute
      :top (gs [:spacing :p48])
      :height :auto
      :right 0
      :bottom 0
      :left 0
      :z-index 0}}

    :main
    {:position :absolute
     :top 0
     :bottom 0
     :left (str sidebar-width "px")
     :right 0
     :overflow-y :auto
     :z-index 0
     :transition "left 0.25s ease-in-out"}

    :nav-menu
    {:margin-bottom (gs [:spacing :p40])}

    :menu-title
    {:padding [(gs [:spacing :p0])
               (gs [:spacing :p20])]
     :color (gs [:colors :true-white])
     :margin-bottom (gs [:spacing :p8])
     :display :block}

    :menu-item
    {:display :block
     :color (gs [:colors :true-white])
     :padding [(gs [:spacing :p8])
               (gs [:spacing :p20])]
     :text-decoration :none
     "&:hover"
     {:background-color (gs [:colors :gray-scale-600])}
     "&.menu-item-active"
     {:background-color (gs [:colors :gray-scale-500])}}}))

(defn- menu-icon [sidebar-open?]
  (let [color-class (if @sidebar-open?
                      (:true-white classes)
                      (:true-black classes))]
    [:div {:class (:items-center classes)
           :style {:cursor :pointer}
           :on-click #(swap! sidebar-open? not)}
     (when @sidebar-open?
       [icons/icon-sm {:class color-class
                       :style {:margin-right (gs [:spacing :p8])}}
        :chevron-left])
     [icons/icon {:class color-class} :bars]]))

(defn- get-admin-path [{:keys [menu-key extra-args]} params]
  (let [{:keys [curriculum-uuid activity-uuid]} params
        path-args (cond-> [menu-key
                           [:curriculum-uuid curriculum-uuid
                            :activity-uuid activity-uuid]]
                    (seq extra-args)
                    (conj extra-args)
                    :aways
                    (flatten))]
    (apply aru/admin-path path-args)))

(defn- render-nav-menu [{:keys [menu-title menu-items router-ctx]}]
  (let [{:keys [route-key params]} router-ctx]
    [:div {:class (:nav-menu classes)}
     [text/title-30 :span {:class (:menu-title classes)} menu-title]
     [:ul
      (for [{:keys [menu-key] :as menu-item} menu-items]
        ^{:key menu-key}
        [:li
         [:a {:href (get-admin-path menu-item params)
              :class [(:menu-item classes)
                      (when (if (set? menu-key)
                              (menu-key route-key)
                              (= menu-key route-key))
                        "menu-item-active")]}
          (route-key->title menu-key)]])]]))

(def ^:private nav-menus
  {:build
   [{:menu-key :activity-overview}
    {:menu-key :activity-resources}
    {:menu-key :activity-moments}
    {:menu-key :activity-assessments
     :extra-args [:question-step :questions]}
    {:menu-key :activity-evaluation
     :extra-args [:question-step :questions]}
    {:menu-key :activity-boosts}
    {:menu-key :activity-advanced}
    {:menu-key :activity-settings}]
   :outcomes
   [{:menu-key :dashboard-list}]
   :reports
   [{:menu-key :reports-line-manager}
    {:menu-key :reports-director}]})

(defn- nav-menu-items [nav-menu route-permissions]
  (->> (nav-menus nav-menu)
       (filter #(get route-permissions (:menu-key %)))))

(defn- sidebar [{:keys [sidebar-open? router-ctx route-permissions]}]
  [:div {:class (:sidebar classes)
         :style {:transform (if @sidebar-open?
                              "translateX(0px)"
                              (str "translateX(-" sidebar-width "px)"))}}
   [:div {:class (:items-center classes)
          :style {:padding (gs [:spacing :p20])}}
    [text/body-30 :span {:style {:flex-grow 1
                                 :color (gs [:colors :gray-scale-300])}}
     "Activity"]
    [menu-icon sidebar-open?]]
   (let [build-nav-items (nav-menu-items :build route-permissions)]
     (when (seq build-nav-items)
       [render-nav-menu {:menu-title "Build"
                         :menu-items build-nav-items
                         :router-ctx router-ctx}]))
   (when (:dashboard route-permissions)
     [render-nav-menu {:menu-title "Outcomes"
                       :menu-items (nav-menus :outcomes)
                       :router-ctx router-ctx}])
   (let [reports-nav-items (nav-menu-items :reports route-permissions)]
     (when (seq reports-nav-items)
       [render-nav-menu {:menu-title "Reports"
                         :menu-items reports-nav-items
                         :router-ctx router-ctx}]))])

(defn- main
  [{:keys [router-ctx activity sidebar-open? route-permissions]}]
  (let [{:keys [route-key resource-status]} router-ctx
        assessment-is-updating
        (re-frame/subscribe [:request/any-dispatching
                             ::>assessments/create-assessment
                             ::>assessments/update-assessment
                             ::>assessments/delete-assessment
                             ::>assessments/create-assessment-question-context
                             ::>assessments/update-assessment-question-context
                             ::>assessments/delete-assessment-question-context
                             ::>assessments/adjust-question-sort-order])

        evaluation-is-updating
        (re-frame/subscribe [:request/any-dispatching
                             ::>evaluations/create-evaluation
                             ::>evaluations/update-evaluation
                             ::>evaluations/delete-evaluation
                             ::>evaluations/create-evaluation-question-context
                             ::>evaluations/update-evaluation-question-context
                             ::>evaluations/delete-evaluation-question-context])
        floating-is-updating
        (re-frame/subscribe [:request/any-dispatching
                             ::>floating/create-floating
                             ::>floating/update-floating
                             ::>floating/delete-floating
                             ::>floating/create-floating-question-context
                             ::>floating/update-floating-question-context
                             ::>floating/delete-floating-question-context])]
    [:<>
     [:div {:class [(:items-center classes)
                    (:title-bar classes)]
            :style {:left (if @sidebar-open?
                            (str sidebar-width "px")
                            (str 0 "px"))}}
      [:div {:style {:flex-grow 1}}
       [:div {:style {:padding-left (gs [:spacing :p20])}
              :class (:items-center classes)}
        (when-not @sidebar-open?
          [:div {:style {:margin-right (gs [:spacing :p20])}}
           [menu-icon sidebar-open?]])
        [text/title-30 :span {} (route-key->title route-key)]]]
      [:div {:style {:padding-right (gs [:spacing :p20])}}
       [fullscreen-toggle/render {:display? false?
                                  :text-prompt "Need more room?"}]]]

     (cond
       (false? (route-key route-permissions))
       [:<>]

       (some? @activity)
       [:div {:class [(:section-container classes)
                      (when (= route-key :activity-moments)
                        (:moments-container classes))]}
        [:div {:style {:display :flex
                       :flex-direction :column
                       :width "100%"}}
         [:div {:class (:section-inner classes)}
          (case route-key
            :activity-overview [activity-overview/render router-ctx]
            :activity-resources [activity-resources/render router-ctx]
            :activity-moments [activity-moments/render router-ctx]
            :activity-assessments [activity-questionnaire/render router-ctx {:questionnaire-create-event ::>assessments/create-assessment
                                                                             :questionnaire-update-event ::>assessments/update-assessment
                                                                             :questionnaire-delete-event ::>assessments/delete-assessment
                                                                             :questionnaire-sort-event ::>assessments/adjust-question-sort-order
                                                                             :question-ctx-create-event ::>assessments/create-assessment-question-context
                                                                             :question-ctx-update-event ::>assessments/update-assessment-question-context
                                                                             :question-ctx-delete-event ::>assessments/delete-assessment-question-context
                                                                             :question-ctx-resource-add-event ::>assessments/add-assessment-question-context-resource
                                                                             :question-ctx-resource-remove-event ::>assessments/remove-assessment-question-context-resource
                                                                             :question-hover-idx-event ::>questionnaires/add-question-hover-idx
                                                                             :question-clear-drag-event ::>questionnaires/questions-clear-drag-state} assessment-is-updating]
            :activity-evaluation [activity-questionnaire/render router-ctx {:questionnaire-create-event ::>evaluations/create-evaluation
                                                                            :questionnaire-update-event ::>evaluations/update-evaluation
                                                                            :questionnaire-delete-event ::>evaluations/delete-evaluation
                                                                            :questionnaire-sort-event ::>evaluations/adjust-question-sort-order
                                                                            :question-hover-idx-event ::>questionnaires/add-question-hover-idx
                                                                            :question-clear-drag-event ::>questionnaires/questions-clear-drag-state
                                                                            :question-ctx-create-event ::>evaluations/create-evaluation-question-context
                                                                            :question-ctx-update-event ::>evaluations/update-evaluation-question-context
                                                                            :question-ctx-delete-event ::>evaluations/delete-evaluation-question-context} evaluation-is-updating]
            :activity-advanced [activity-advanced/render router-ctx]
            :activity-boosts [activity-boosts/render router-ctx]
            :activity-boost-step-workflow [activity-boost-workflow/render router-ctx]
            :activity-settings [activity-settings/render router-ctx]
            :edit-activity-boost [activity-boost/render router-ctx]
            :edit-activity-boost-questionnaire [activity-questionnaire/render router-ctx {:questionnaire-create-event ::>floating/create-floating
                                                                                          :questionnaire-update-event ::>floating/update-floating
                                                                                          :questionnaire-delete-event ::>floating/delete-floating
                                                                                          :questionnaire-sort-event ::>floating/adjust-question-sort-order
                                                                                          :question-hover-idx-event ::>questionnaires/add-question-hover-idx
                                                                                          :question-clear-drag-event ::>questionnaires/questions-clear-drag-state
                                                                                          :question-ctx-resource-add-event ::>floating/add-floating-question-context-resource
                                                                                          :question-ctx-resource-remove-event ::>floating/remove-floating-question-context-resource
                                                                                          :question-ctx-create-event ::>floating/create-floating-question-context
                                                                                          :question-ctx-update-event ::>floating/update-floating-question-context
                                                                                          :question-ctx-delete-event ::>floating/delete-floating-question-context} floating-is-updating]

            :dashboard-list [looker-dashboards/render router-ctx]
            :dashboard [looker-dashboard/render router-ctx]

            :reports-line-manager [line-manager-report/render router-ctx]
            :reports-director [director-report/render router-ctx])]
         (when-not (= route-key :activity-moments)
           [footer])]]

       (and (ku/all-resources-loaded? resource-status)
            (nil? @activity))
       [not-found/not-found {:source :activity}]

       :else
       [loading/render
        {:loading-message "Please wait while we fetch your activity"}])]))

(defn- curriculum-banner
  [{:keys [curriculum-uuid activity route-params]}]
  (let [legacy-curriculum-sub
        (re-frame/subscribe
         [:curricula/curriculum
          {:curriculum-uuid curriculum-uuid}])

        curriculum-sub
        (re-frame/subscribe
         [::kyber-db/get-record
          {:model :curriculum
           :ident curriculum-uuid}])

        can-edit-activity
        (re-frame/subscribe [:authentication/has-access?
                             :activity/can-edit
                             {:curriculum-uuid curriculum-uuid}])
        curriculum
        (or @curriculum-sub @legacy-curriculum-sub)]
    [banner/render
     {:route-params route-params
      :curriculum (when (some? curriculum)
                    (ku/nsmap->map curriculum))
      :activity (when (some? @activity)
                  (-> @activity
                      (dissoc :curriculum/uuid)
                      ku/nsmap->map))
      :show-edit @can-edit-activity
      :back-link (aru/admin-path :curriculum-activities
                                 :curriculum-uuid curriculum-uuid)
      :back-copy "< Back to curriculum"}]))

(defn- make-access-subscription [curriculum-uuid action]
  (re-frame/subscribe
   [:authentication/has-access? action {:curriculum-uuid curriculum-uuid}]))

(defn render
  [{:keys [params]}]
  (let [{:keys [curriculum-uuid activity-uuid]} params
        legacy-activity-sub
        (re-frame/subscribe
         [:activities/activity
          {:curriculum-uuid curriculum-uuid
           :activity-uuid activity-uuid}])

        activity-sub
        (re-frame/subscribe
         [::kyber-db/get-record
          {:model :activity
           :ident activity-uuid}])

        sidebar-open? (reagent/atom true)]
    (fn [{:keys [params route-key] :as router-ctx}]
      (let [{:activity/keys [can-view-insights
                             can-view-overview
                             can-view-director-report
                             can-manage-assessments
                             can-manage-boosts
                             can-manage-evaluation
                             can-manage-moments
                             can-edit
                             can-edit-advanced
                             can-manage-plo
                             can-manage-learning-objectives]}
            (into {} (map #(vector % @(make-access-subscription curriculum-uuid %))
                          [:activity/can-view-insights
                           :activity/can-view-overview
                           :activity/can-manage-assessments
                           :activity/can-manage-boosts
                           :activity/can-manage-evaluation
                           :activity/can-manage-moments
                           :activity/can-edit
                           :activity/can-edit-advanced
                           :activity/can-manage-plo
                           :activity/can-manage-learning-objectives
                           :activity/can-view-director-report]))

            route-permissions
            {:activity-overview (or can-manage-plo
                                    can-view-overview
                                    can-manage-learning-objectives)
             :activity-resources (or can-manage-moments
                                     can-manage-plo)
             :activity-moments (or can-manage-moments
                                   can-edit-advanced)
             :activity-assessments can-manage-assessments
             :activity-evaluation can-manage-evaluation
             :activity-boosts can-manage-boosts
             :activity-advanced can-edit-advanced
             :activity-settings can-edit
             :dashboard can-view-insights
             :reports-line-manager can-edit-advanced
             :reports-director can-view-director-report}

            activity
            (if (#{:activity-resources
                   :activity-settings
                   :activity-moments
                   :activity-overview
                   ;;:activity-assessments
                   ;;:activity-evaluation
                   } route-key)
              activity-sub
              legacy-activity-sub)]
        [:<>
         [:div {:class [(:page-container classes)
                        (:full-height classes)]}
          [resource-viewer/render]
          [curriculum-banner {:curriculum-uuid curriculum-uuid
                              :activity activity
                              :route-params params}]
          [:div {:class [(:full-height classes)
                         (:items-center classes)]
                 :style {:position :relative}}
           [sidebar {:router-ctx router-ctx
                     :route-permissions route-permissions
                     :sidebar-open? sidebar-open?}]
           [:div {:class (:main classes)
                  :style {:left (if @sidebar-open?
                                  (str sidebar-width "px")
                                  (str 0 "px"))
                          :flex-direction :column
                          :display :flex}}
            [main {:router-ctx router-ctx
                   :route-permissions route-permissions
                   :sidebar-open? sidebar-open?
                   :activity activity}]]]]]))))
